import req from '@sportninja/common/api/request'

/**
 * Placeholder image paths for different entity types
 */
export const PLACEHOLDER_IMAGES = {
  user: '/images/generic_placeholders/placeholder_player.png',
  organization: '/images/generic_placeholders/placeholder_organization.png',
  team: '/images/generic_placeholders/placeholder_team.png',
  schedule: '/images/generic_placeholders/placeholder_competition.png',
} as const

/**
 * Likes a comment by sending a POST request
 * @param commentId - The ID of the comment to like
 * @returns Promise that resolves to true on success
 */
export const likeComment = async (commentId: string) => {
  await req(`/social-feed-reply/${commentId}/like`, {
    method: 'POST',
  })
  return true
}

/**
 * Removes a like from a comment by sending a DELETE request
 * @param commentId - The ID of the comment to unlike
 * @returns Promise that resolves to true on success
 */
export const dislikeComment = async (commentId: string) => {
  await req(`/social-feed-reply/${commentId}/like`, {
    method: 'DELETE',
  })
  return true
}

/**
 * Deletes a comment by sending a DELETE request
 * @param commentId - The ID of the comment to delete
 * @returns Promise that resolves to true on success
 */
export const deleteComment = async (commentId: string) => {
  await req(`/social-feed-reply/${commentId}`, {
    method: 'DELETE',
  })
  return true
}

/**
 * Parses a URL and returns the host without 'www.' prefix
 * @param url - The URL to parse
 * @returns The formatted host name or the original URL if parsing fails
 */
export const parseUrlHost = (url: string): string => {
  try {
    const parsedUrl = new URL(url)
    return parsedUrl.host.replace(/^www\./, '')
  } catch (error) {
    console.error('Error parsing URL:', error)
    return url
  }
}

/**
 * Gets the appropriate placeholder image for an entity type
 * @param entityType - The type of entity ('team' | 'schedule' | 'organization')
 * @param isUser - Whether to return the user placeholder
 * @returns The path to the placeholder image
 */
export const getPlaceholderImage = (
  entityType: 'team' | 'schedule' | 'organization',
  isUser = false
): string => {
  if (isUser) {
    return PLACEHOLDER_IMAGES.user
  }
  return PLACEHOLDER_IMAGES[entityType]
}

/**
 * Tracks a post view event by sending analytics data
 * This is a fire-and-forget operation that should not block or show errors to users
 * @param postId - The ID of the post being viewed
 * @param type - The type of analytics event ('social_feed', 'email', or 'push')
 * @returns Promise that resolves to void
 */
export const trackPostView = async (
  postId: string,
  type: 'social_feed' | 'email' | 'push' = 'social_feed'
): Promise<void> => {
  try {
    console.log(
      '🔄 trackPostView: Tracking view for post',
      postId,
      'with type:',
      type
    )

    // Build query parameter for type
    const queryParam = type ? `?type=${type}` : ''

    await req(`/social-feed/${postId}/click${queryParam}`, {
      method: 'POST',
    })
    console.log(
      '✅ trackPostView: Successfully tracked view for post',
      postId,
      'with type:',
      type
    )
  } catch (error) {
    // Silent failure - only log to console, don't throw or show to user
    console.log(
      '⚠️ trackPostView: Failed to track view for post',
      postId,
      'with type:',
      type,
      error
    )
  }
}

/**
 * Analytics data structure from API
 */
export interface PostAnalyticsData {
  id: string
  push_notification_clicks: number
  email_clicks: number
  social_feed_impressions: number
  social_feed_clicks: number
  notification_email_sent: number
  notification_push_sent: number
  notification_sms_sent: number
}

/**
 * API response structure for analytics endpoint
 */
export interface PostAnalyticsResponse {
  data: PostAnalyticsData
}

/**
 * Fetches analytics data for a specific post
 * @param postId - The ID of the post to fetch analytics for
 * @returns Promise that resolves to analytics data or null on error
 */
export const fetchPostAnalytics = async (
  postId: string
): Promise<PostAnalyticsData | null> => {
  try {
    console.log('🔄 fetchPostAnalytics: Fetching analytics for post', postId)
    const response = await req(`/social-feed/${postId}/analytics`, {
      method: 'GET',
    })
    console.log(
      '✅ fetchPostAnalytics: Successfully fetched analytics',
      response.data
    )
    return response.data
  } catch (error) {
    // Log error but don't throw - return null to indicate failure
    console.log(
      '⚠️ fetchPostAnalytics: Failed to fetch analytics for post',
      postId,
      error
    )
    return null
  }
}
